'use client';

import { useCallback, useRef, useState } from 'react';
import { Box, Typography, Button, alpha, Stack } from '@mui/material';
import { CloudUpload, Add } from '@mui/icons-material';
import { useMediaBrowserV2 } from '@/app/features/shared/media-browser-v2/context/MediaBrowserV2Context';

export default function MediaUploadZone() {
    const { addPendingUploads, uploadAllPending, state } = useMediaBrowserV2();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            addPendingUploads(files);
        }
    }, [addPendingUploads]);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set dragging false if leaving the component entirely
        if (e.currentTarget === e.target) {
            setIsDragging(false);
        }
    }, []);

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            addPendingUploads(files);
        }
        // Reset input value to allow selecting the same file again
        e.target.value = '';
    }, [addPendingUploads]);

    const handleUploadAll = useCallback(() => {
        uploadAllPending();
    }, [uploadAllPending]);

    const pendingCount = state.pendingUploads.filter(p => p.uploadStatus === 'pending').length;

    return (
        <Box>
            {/* Drop Zone */}
            <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onClick={handleClick}
                className="cursor-pointer rounded-lg p-8 transition-all"
                sx={{
                    border: (theme) => `2px dashed ${isDragging ? theme.palette.primary.main : alpha(theme.palette.divider, 0.5)}`,
                    backgroundColor: isDragging
                        ? (theme) => alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    },
                }}
            >
                <Stack spacing={2} alignItems="center">
                    <CloudUpload
                        sx={{
                            fontSize: 64,
                            color: isDragging ? 'primary.main' : 'text.disabled',
                            transition: 'color 0.2s ease',
                        }}
                    />
                    <Typography
                        variant="h6"
                        color={isDragging ? 'primary' : 'textSecondary'}
                        align="center"
                    >
                        {isDragging
                            ? 'Dateien hier ablegen'
                            : 'Medien hier ablegen oder klicken'}
                    </Typography>
                    <Typography variant="body2" color="text.disabled" align="center">
                        Unterstützt werden: Bilder, Videos und PDFs
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-2 pointer-events-none"
                    >
                        Dateien auswählen
                    </Button>
                </Stack>
            </Box>

            {/* Upload All Button */}
            {pendingCount > 0 && (
                <Box className="mt-4">
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        startIcon={<CloudUpload />}
                        onClick={handleUploadAll}
                    >
                        Alle hochladen ({pendingCount})
                    </Button>
                </Box>
            )}

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,application/pdf"
                className="hidden"
                onChange={handleFileSelect}
            />
        </Box>
    );
}
